import { useCallback, useEffect, useMemo, useState } from "react";
import { quoteWorkspaceEnrichmentApi } from "../../api/workspaceApi";
import { errorDetail } from "../../helper/errorDetail";

// Re-quote this long before expiry so a user who reads the cost for a while
// does not submit a quote the backend has already expired.
const REFRESH_BEFORE_EXPIRY_MS = 30 * 1000;
// Floor for that timer: a client clock running ahead of the server would
// otherwise see every fresh quote as expired and re-quote in a tight loop.
const MIN_REFRESH_MS = 60 * 1000;
const QUOTE_DEBOUNCE_MS = 250;

const quoteKey = (workspaceId, llmProvider, companyIds) =>
  JSON.stringify([workspaceId, llmProvider || "", Array.from(new Set(companyIds || [])).sort()]);

/**
 * The backend's authoritative credit quote (BE #517 / FE #588) for exactly the
 * company set a submission would send. `status` is "idle" | "loading" | "ready"
 * | "error"; only "ready" carries `quote`. `matches(ids, provider)` says whether
 * a submission is still the run that was quoted.
 */
export function useEnrichmentQuote({ workspaceId, companyIds, llmProvider, enabled = true }) {
  const key = quoteKey(workspaceId, llmProvider, companyIds);
  const hasScope = Boolean(workspaceId) && (companyIds || []).length > 0;
  const [state, setState] = useState({ status: "idle", key: null });
  const [generation, setGeneration] = useState(0);
  const refresh = useCallback(() => setGeneration((value) => value + 1), []);

  useEffect(() => {
    if (!enabled || !hasScope) {
      setState({ status: "idle", key: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading", key });
    const timer = setTimeout(async () => {
      const res = await quoteWorkspaceEnrichmentApi(workspaceId, {
        company_ids: Array.from(new Set(companyIds)),
        llm_provider: llmProvider,
      });
      if (cancelled) return;
      if (res?.status === 200 && res.data?.quote_token) {
        setState({ status: "ready", key, quote: res.data });
      } else {
        setState({ status: "error", key, message: errorDetail(res?.data?.detail, "Could not price this enrichment.") });
      }
    }, QUOTE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `key` captures workspaceId, llmProvider and the company set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, hasScope, generation]);

  const expiresAt = state.status === "ready" ? Date.parse(state.quote.expires_at) : NaN;
  useEffect(() => {
    if (!Number.isFinite(expiresAt)) return undefined;
    const timer = setTimeout(refresh, Math.max(MIN_REFRESH_MS, expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS));
    return () => clearTimeout(timer);
  }, [expiresAt, refresh]);

  const pendingStatus = enabled && hasScope ? "loading" : "idle";
  const current = useMemo(
    () => (state.key === key ? state : { status: pendingStatus }),
    [state, key, pendingStatus]
  );
  const matches = useCallback(
    (ids, provider) => current.status === "ready" && quoteKey(workspaceId, provider, ids) === key,
    [current.status, workspaceId, key]
  );
  // The token to submit with, or null (and a re-quote) when the run is no longer the one quoted.
  const tokenFor = useCallback((ids, provider) => {
    if (matches(ids, provider)) return current.quote.quote_token;
    refresh();
    return null;
  }, [matches, current.quote, refresh]);
  return useMemo(() => ({ ...current, refresh, matches, tokenFor }), [current, refresh, matches, tokenFor]);
}

export const QUOTE_SCOPE_CHANGED_MESSAGE =
  "The companies or model changed since this cost was quoted. Review the updated cost and try again.";

const isQuoteRejection = (res) => String(res?.data?.detail?.error_code || "").startsWith("QUOTE_");

/** User-facing message for a refused POST /workspaces/{id}/enrich. */
export function enrichSubmitErrorMessage(res) {
  if (res?.status === 402) return "Insufficient credits to run this enrichment.";
  if (res?.status === 409 && isQuoteRejection(res)) {
    return `${errorDetail(res?.data?.detail, "The price changed.")} The updated cost is shown before you run it again.`;
  }
  if (res?.status === 409) return "You already have an active batch running. Please wait for it to finish.";
  return errorDetail(res?.data?.detail, "Enrichment failed to start");
}
